import type { EnvironmentRenderConfig } from "../render/environment.ts";

/**
 * M4 atmosphere values are intentionally centralized for the visual-review
 * gate. The running clock is slow enough to preserve a place's mood while
 * debug scenarios can freeze and scrub exact hours.
 */
export const ENVIRONMENT_RENDER_CONFIG: EnvironmentRenderConfig =
  Object.freeze({
    dayLengthSeconds: 72 * 60,
    scrubStepHours: 0.5,
    fogNearMeters: 52,
    // Keep the wooded Three Crosses / Kalnu Park ridge legible from the
    // source-terrain vista bookmark while retaining a clearly atmospheric
    // separation from the Old Town foreground.
    fogFarDayMeters: 580,
    fogFarNightMeters: 320,
    // Flight remains useful for geographic review, but distant procedural
    // detail now recedes before it becomes a uniformly sharp horizon band.
    flightFogDistanceScale: 2,
    sunDistanceMeters: 95,
    shadowFocusHeightMeters: 2,
    exposureDay: 1.06,
    exposureDusk: 1.22,
    exposureNight: 1.42,
    day: {
      sky: 0x91a9ad,
      fog: 0xaab5ad,
      hemisphereSky: 0xc8d8d5,
      hemisphereGround: 0x7d7b68,
      sun: 0xffe3bc,
      fill: 0x718a83,
      hemisphereIntensity: 2.05,
      sunIntensity: 2.65,
      fillIntensity: 0.38,
    },
    dusk: {
      sky: 0x806f72,
      fog: 0x9a8881,
      hemisphereSky: 0xc3aca3,
      hemisphereGround: 0x665c57,
      sun: 0xf2ad78,
      fill: 0x78868b,
      hemisphereIntensity: 1.35,
      sunIntensity: 1.95,
      fillIntensity: 0.62,
    },
    night: {
      sky: 0x182538,
      fog: 0x344251,
      hemisphereSky: 0x8196b2,
      hemisphereGround: 0x34414b,
      sun: 0xa9bddb,
      fill: 0x60788f,
      hemisphereIntensity: 0.82,
      sunIntensity: 0.42,
      fillIntensity: 0.78,
    },
    atmosphere: {
      radiusMeters: 2_400,
      // Stored as the small dot-product interval used by the shader. These
      // produce restrained discs rather than oversized fantasy suns/moons.
      sunDiskSize: 0.00016,
      moonDiskSize: 0.00024,
      starDensity: 0.0075,
      seasons: {
        spring: {
          upperSky: 0x6f99aa,
          cloudLight: 0xe8eadf,
          cloudShadow: 0x9caca9,
          cloudCoverage: 0.42,
          cloudDensity: 0.68,
          cloudScale: 2.85,
          hazeStrength: 0.27,
        },
        summer: {
          upperSky: 0x557f91,
          cloudLight: 0xe7e5d8,
          cloudShadow: 0x8b9b9b,
          cloudCoverage: 0.34,
          cloudDensity: 0.6,
          cloudScale: 3.05,
          hazeStrength: 0.18,
        },
        autumn: {
          upperSky: 0x667d84,
          cloudLight: 0xd1cec0,
          cloudShadow: 0x7c8584,
          cloudCoverage: 0.62,
          cloudDensity: 0.78,
          cloudScale: 2.55,
          hazeStrength: 0.4,
        },
        winter: {
          upperSky: 0x8398a2,
          cloudLight: 0xd5dcdd,
          cloudShadow: 0x8d999f,
          cloudCoverage: 0.78,
          cloudDensity: 0.88,
          cloudScale: 2.3,
          hazeStrength: 0.55,
        },
      },
    },
    seasons: {
      spring: {
        sky: 0xa8bec1,
        fog: 0xb8c4b7,
        hemisphereSky: 0xd6e2dc,
        hemisphereGround: 0x82916f,
        sun: 0xffecca,
        fill: 0x78938b,
        paletteBlend: 0.28,
        lightBlend: 0.22,
        fogDistanceScale: 1.05,
        exposureScale: 1.03,
        sunHeightScale: 0.9,
      },
      summer: {
        sky: 0x91a9ad,
        fog: 0xaab5ad,
        hemisphereSky: 0xc8d8d5,
        hemisphereGround: 0x7d7b68,
        sun: 0xffe3bc,
        fill: 0x718a83,
        paletteBlend: 0,
        lightBlend: 0,
        fogDistanceScale: 1,
        exposureScale: 1,
        sunHeightScale: 1,
      },
      autumn: {
        sky: 0x7f8e90,
        fog: 0xa49d8b,
        hemisphereSky: 0xc0b9a9,
        hemisphereGround: 0x75664f,
        sun: 0xffc17d,
        fill: 0x7b827b,
        paletteBlend: 0.34,
        lightBlend: 0.3,
        fogDistanceScale: 0.84,
        exposureScale: 1,
        sunHeightScale: 0.72,
      },
      winter: {
        sky: 0xb3bdc2,
        fog: 0xc9ceca,
        hemisphereSky: 0xd8dfe0,
        hemisphereGround: 0x98a2a2,
        sun: 0xf3ead3,
        fill: 0x98a9ad,
        paletteBlend: 0.62,
        lightBlend: 0.56,
        fogDistanceScale: 0.72,
        exposureScale: 1.08,
        sunHeightScale: 0.5,
      },
    },
  });
